// src/components/StrategyBuilder/StepDefineSignals.jsx
import React, { useState } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent,
  TextField, IconButton, Chip, Divider, Alert, Paper,
  Grid, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  FlashOn as SignalIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
} from '@mui/icons-material';

const StepDefineSignals = ({ data, onChange }) => {
  const [newSignal, setNewSignal] = useState({ 
    algo: '', 
    direction: 'LONG', 
    description: '' 
  });

  const addSignal = () => {
    if (!newSignal.algo.trim()) return;
    
    const signal = {
      id: Date.now(),
      algo: newSignal.algo.trim(),
      direction: newSignal.direction,
      description: newSignal.description.trim(),
    };
    
    onChange({
      ...data,
      expected_signals: [...(data.expected_signals || []), signal]
    });
    
    setNewSignal({ algo: '', direction: 'LONG', description: '' });
  };

  const removeSignal = (signalId) => {
    onChange({
      ...data,
      expected_signals: (data.expected_signals || []).filter(s => s.id !== signalId)
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.name === 'signalAlgo') {
      e.preventDefault();
      if (newSignal.algo.trim()) {
        addSignal();
      }
    }
  };

  const getSignalKey = (signal) => `${signal.algo}:${signal.direction}`;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SignalIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800, 
              color: '#1e293b',
              mb: 0.5,
            }}
          >
            Define Expected Signals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Specify the signals your strategy will track from TradingView webhooks
          </Typography>
        </Box>
        <Chip
          label={`${(data.expected_signals || []).length} Signal${(data.expected_signals || []).length !== 1 ? 's' : ''}`}
          sx={{
            bgcolor: (data.expected_signals || []).length > 0 ? '#fef3c7' : '#f1f5f9',
            color: (data.expected_signals || []).length > 0 ? '#d97706' : '#64748b',
            fontWeight: 700,
            fontSize: '0.875rem',
            height: 32,
            px: 1,
          }}
        />
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {/* Info Alert */}
      <Alert 
        icon={<InfoIcon />}
        severity="info" 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          bgcolor: '#eff6ff',
          border: '1px solid #bfdbfe',
          '& .MuiAlert-icon': {
            color: '#3b82f6',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1e3a8a' }}>
          New Signal Format: Algo + Direction
        </Typography>
        <Typography variant="body2" sx={{ color: '#1e40af', mb: 1 }}>
          Define signals using <strong>algorithm name</strong> and <strong>direction</strong> (LONG/SHORT). This matches the professional format from TradingView webhooks.
        </Typography>
        <Typography variant="body2" component="div" sx={{ color: '#1e40af', fontFamily: 'monospace', fontSize: '0.85rem' }}>
          <strong>Examples:</strong><br />
          • lux_algo + LONG<br />
          • cobra_algo + SHORT<br />
          • market_wave + LONG<br />
          • smart_trail + SHORT
        </Typography>
      </Alert>

      {/* Add Signal Form */}
      <Card sx={{ mb: 4, borderRadius: 3, border: '2px dashed #e2e8f0' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5, color: '#1e293b' }}>
            Add New Signal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                name="signalAlgo"
                label="Algorithm Name"
                placeholder="e.g., lux_algo, cobra_algo"
                value={newSignal.algo}
                onChange={(e) => setNewSignal({ ...newSignal, algo: e.target.value })}
                onKeyPress={handleKeyPress}
                helperText="Must match your TradingView indicator"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    '& input': {
                      fontWeight: 600,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontWeight: 600 }}>Direction</InputLabel>
                <Select
                  value={newSignal.direction}
                  onChange={(e) => setNewSignal({ ...newSignal, direction: e.target.value })}
                  label="Direction"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    },
                  }}
                >
                  <MenuItem value="LONG">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LongIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 700, color: '#10b981' }}>LONG</Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="SHORT">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ShortIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 700, color: '#ef4444' }}>SHORT</Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description (optional)"
                placeholder="e.g., Lux long signal"
                value={newSignal.description}
                onChange={(e) => setNewSignal({ ...newSignal, description: e.target.value })}
                onKeyPress={handleKeyPress}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addSignal}
                disabled={!newSignal.algo.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 2,
                  boxShadow: newSignal.algo.trim() 
                    ? '0 4px 12px rgba(102, 126, 234, 0.25)'
                    : 'none',
                  '&:hover': {
                    background: newSignal.algo.trim()
                      ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                      : undefined,
                    transform: newSignal.algo.trim() ? 'translateY(-2px)' : 'none',
                  },
                  transition: 'all 0.2s',
                }}
              >
                Add Signal
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Signal List */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
        Expected Signals ({(data.expected_signals || []).length})
      </Typography>

      {(data.expected_signals || []).length === 0 ? (
        <Paper
          sx={{
            textAlign: 'center',
            py: 6,
            px: 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3,
            border: '2px dashed #cbd5e1',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <SignalIcon sx={{ fontSize: 32, color: '#d97706' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            No Signals Added Yet
          </Typography>
          <Typography color="text.secondary">
            Add at least 2 signals to continue building your strategy
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {(data.expected_signals || []).map((signal, index) => {
            const isLong = signal.direction === 'LONG';
            const DirectionIcon = isLong ? LongIcon : ShortIcon;
            const directionColor = isLong ? '#10b981' : '#ef4444';
            const directionBg = isLong ? '#d1fae5' : '#fee2e2';
            
            return (
              <Card key={signal.id} sx={{ borderRadius: 2, border: '2px solid #e2e8f0' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="start">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          bgcolor: '#fef3c7',
                          border: '2px solid #fde68a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          color: '#d97706',
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            label={signal.algo}
                            sx={{
                              background: 'linear-gradient(135deg, #667eea15, #764ba208)',
                              color: '#667eea',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                            }}
                          />
                          <Chip
                            icon={<DirectionIcon sx={{ fontSize: '16px !important' }} />}
                            label={signal.direction}
                            sx={{
                              bgcolor: directionBg,
                              color: directionColor,
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              height: 24,
                              '& .MuiChip-icon': {
                                color: directionColor,
                              },
                            }}
                          />
                        </Stack>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#64748b', 
                            display: 'block', 
                            mt: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                          }}
                        >
                          Match Key: {getSignalKey(signal)}
                        </Typography>
                        {signal.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {signal.description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => removeSignal(signal.id)}
                      sx={{
                        color: '#ef4444',
                        bgcolor: '#fef2f2',
                        border: '2px solid #fee2e2',
                        '&:hover': {
                          bgcolor: '#fee2e2',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Helper Info */}
      {(data.expected_signals || []).length > 0 && (data.expected_signals || []).length < 2 && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2.5,
            bgcolor: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoIcon sx={{ color: '#d97706', mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                Add More Signals
              </Typography>
              <Typography variant="body2" sx={{ color: '#78350f' }}>
                You need at least 2 signals to create trigger conditions for your strategy.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* TradingView Webhook Example */}
      {(data.expected_signals || []).length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2.5,
            bgcolor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoIcon sx={{ color: '#3b82f6', mt: 0.3 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e3a8a', mb: 1 }}>
                TradingView Webhook Format
              </Typography>
              <Typography variant="body2" sx={{ color: '#1e40af', mb: 1.5 }}>
                Your webhook message should send signals in this format:
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#1e293b',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: '#f1f5f9',
                  overflowX: 'auto',
                }}
              >
                <pre style={{ margin: 0 }}>
{`{
  "strategy_id": "${data.strategy_id || 'YOUR_STRATEGY'}",
  "signals": {
    "algo": "lux_algo",
    "direction": "LONG",
    "timeframe": "{{interval}}",
    "settings": {
      "sensitivity": 12
    }
  }
}`}
                </pre>
              </Paper>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default StepDefineSignals;
